import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {Subscription, takeUntil} from "rxjs";
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CcBaseComponent} from "../../../../../../crystal-elements/cc-base.component";
import {CrystalClearFrontUrl} from "../../../../../../shared/pages/crystal-clear-front-url";
import {TradeSelectionTableComponent} from "../selection-table/trade-selection-table.component";
import {CookieNames} from "../../../../../../shared/cookie/cookie-names";
import {Dialog} from "primeng/dialog";
import {WebTradeOrderView} from "../../../../../../model/internal/web-trade-order-view";
import {ClearingInfoLoadRequest} from "../../../../../../model/clearing/clearing-info-load-request";
import {TableColumnConfig} from "../../../../../../model/api/table-column-config";
import {ViewConfig} from "../../../../../../model/api/view-config";
import {ClearingSelectionInfo} from "../../../../../../model/clearing/clearing-selection-info";
import {ClearingTradeInfoService} from "../../../../../../services/clearing/clearing-trade-info.service";
import {AppConfigService} from "../../../../../../services/app-config.service";
import {CcStorageService} from "../../../../../../services/common/cc-storage.service";
import {SelectionType} from "../../../../../../crystal-elements/cc-vscroll-table/cc-vscroll-table.component";
import {CtrlAMode} from "../../trade-table.component";
import {TableRequest} from "../../../../../../model/api/table-request";

@Component({
  selector: 'app-trade-selection-table-dialog',
  templateUrl: './trade-selection-table-dialog.component.html',
  styleUrls: ['./trade-selection-table-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeSelectionTableDialogComponent extends CcBaseComponent {

  dialogTitle: string = 'page.clearing.trade.selection.dialog_title';

  @Input()
  transPartKey: string = 'trade.selection';
  CrystalClearFrontUrl = CrystalClearFrontUrl;

  inProgressCount: number = 0;

  @Input()
  isReadOnly: boolean = true;

  @Input()
  userView: WebTradeOrderView = undefined;

  @Input()
  tradeTableCurrentRequest: TableRequest;

  @Input()
  ctrlAMode: CtrlAMode = 'ALL_OPTIM';
  @Input()
  selectionType: SelectionType = 'CUSTOM';

  @Input()
  viewConfig: ViewConfig;

  @Input()
  tableLoading: boolean = true;
  @Output()
  tableLoadingChange = new EventEmitter<boolean>();

  filterWithValuesOptions: any[] = [];
  filterWithValue: any[] = [];

  @Output()
  applySelectionChange = new EventEmitter<string[]>();

  @Output()
  dialogManuallyClosed = new EventEmitter<boolean>();

  @Output()
  doSingleAllocate = new EventEmitter<string[]>();
  @Output()
  doSplitAllocate = new EventEmitter<string[]>();

  selectionTradeDialogVisible = false;

  @Input()
  selectedTradeIds: string[];
  pastSelectedTradeIds: string[];

  splitAllocationEnabledValue: boolean = false;
  singleAllocationEnabledValue: boolean = false;
  applyEnabledValue: boolean = false;

  @ViewChild('tradeSelectionTable')
  tradeSelectionTable: TradeSelectionTableComponent;

  @ViewChild('dialog')
  dialog: Dialog;

  selectionBase: boolean = true;

  origin: string = WebTradeOrderView.ORIGIN.TRADE_AGG;

  visibleColumns: string[] = [];

  private refreshTimeout?: ReturnType<typeof setTimeout>;
  private reloadTimeout?: ReturnType<typeof setTimeout>;
  private selectionInfoSubscription?: Subscription;
  private selectionInfoRequestVersion: number = 0;
  private lastReloadKey?: string;
  private selectionInfoTimeout?: ReturnType<typeof setTimeout>;
  private lastSelectionInfoIds?: string[];

  constructor(cdref: ChangeDetectorRef,
              messageService: MessageService,
              translateService: TranslateService,
              private storageService: CcStorageService,
              private clearingTradeInfoService: ClearingTradeInfoService,
              private appConfigService: AppConfigService) {
    super(cdref, messageService, translateService);
    this.filterWithValue = [...appConfigService?.tradeAggregationColumns
      ?.filter(value => value.defaultIgnore && value.filterable)
      .map(value => value.field)];
  }

  protected initData() {
    super.initData();
    this.filterWithValuesOptions = [];
    this.appConfigService.tradeAggregationColumns.filter(value => value.filterable).forEach(value => this.filterWithValuesOptions.push(
      {
        id: value.field,
        text: value.label,
        disabled: false
      }
    ))

    // if (!this.appConfigService.clearingTradeSelectionMandatoryFields)
    //   this.filterWithValue = [...this.appConfigService.clearingTradeSelectionMandatoryFields];
  }

  ngOnDestroy(): void {
    this.clearPendingAsyncWork();
    this.clearSelectionInfoRequest(true);
    super.ngOnDestroy();
  }

  selectionChange(ids: string[]) {
    this.scheduleReloadClearingSectionInfos(ids);
  }

  applySelection(ids: string[]) {
    this.applySelectionChange.emit(ids);
  }

  singleAllocate(ids: string[]) {
    this.doSingleAllocate.emit(ids);
  }

  splitAllocate(ids: string[]) {
    this.doSplitAllocate.emit(ids);
  }

  forceReload() {

    this.filterWithValue = JSON.parse(JSON.stringify(this.filterWithValue));
    this.pastSelectedTradeIds = undefined;
    this.lastReloadKey = undefined;
    this.scheduleRefresh(() => {
      this.selectionBase = !this.tradeSelectionTable?.isCtrlAModeApplied();
      if (this.tradeSelectionTable) {
        this.tradeSelectionTable.refreshColumns();
        this.tradeSelectionTable.refreshSelectionInfos();
        this.reloadTable(true);
      }
    });
  }

  show() {

    this.visibleColumns = [];
    if (this.userView) {
      if (this.storageService.check(CookieNames.TRADE_SELECTION_FILTER_SELECTED_ + this.userView.id)) {
        this.filterWithValue = JSON.parse(this.storageService.get(CookieNames.TRADE_SELECTION_FILTER_SELECTED_ + this.userView.id));
      }
      if (this.storageService.check(CookieNames.CLEARING_VISIBLE_COLUMNS_ + this.userView.id + '_' + this.origin)) {
        this.visibleColumns = JSON.parse(this.storageService.get(CookieNames.CLEARING_VISIBLE_COLUMNS_ + this.userView.id + '_' + this.origin));
      }
    }

    this.selectionTradeDialogVisible = true;
    if (this.tradeSelectionTable)
      this.tradeSelectionTable.selectionTradeDialogVisible = true;
    this.markForCheck();
  }

  close() {
    this.clearPendingAsyncWork();
    this.clearSelectionInfoRequest(true);
    this.resetLoadingState();
    this.dialogManuallyClosed.emit(true);
    this.selectionTradeDialogVisible = false;
    if (this.tradeSelectionTable) {
      this.tradeSelectionTable.selectionTradeDialogVisible = false;
    }
    this.markForCheck();
  }

  hide() {
    this.clearPendingAsyncWork();
    this.clearSelectionInfoRequest(true);
    this.resetLoadingState();
    this.selectionTradeDialogVisible = false;
    if (this.tradeSelectionTable) {
      this.tradeSelectionTable.selectionTradeDialogVisible = false;
    }
    this.markForCheck();
  }

  protected filterChanged(value) {

    this.tradeSelectionTable?.setLoading();
    this.pastSelectedTradeIds = undefined;
    this.lastReloadKey = undefined;
    this.filterWithValue = value ? [...value] : [];
    if (this.userView) {
      this.storageService.set(CookieNames.TRADE_SELECTION_FILTER_SELECTED_ + this.userView.id, JSON.stringify(this.filterWithValue));
    }
    this.scheduleRefresh(() => {
      if (this.tradeSelectionTable) {
        this.tradeSelectionTable.refreshColumns();
        this.tradeSelectionTable.refreshSelectionInfos();
        this.reloadTable(true);
      }
    });
  }

  reloadTable(force: boolean = false) {
    this.selectionBase = !this.tradeSelectionTable?.isCtrlAModeApplied();
    if (!this.tradeSelectionTable) {
      return;
    }

    const reloadKey = this.buildReloadKey();
    if (!force && this.lastReloadKey === reloadKey) {
      return;
    }

    this.lastReloadKey = reloadKey;
    this.pastSelectedTradeIds = this.selectedTradeIds ? [...this.selectedTradeIds] : [];
    this.scheduleReload();
  }

  onVisibilityChange(event) {
    this.selectionTradeDialogVisible = event;

    if (!event) {
      this.clearPendingAsyncWork();
      this.clearSelectionInfoRequest(true);
      this.resetLoadingState();
      this.dialogManuallyClosed.emit(true);
    }

    if (this.tradeSelectionTable) {
      this.tradeSelectionTable.selectionTradeDialogVisible = event;
    }
  }

  clearFilter() {
    this.filterWithValue.splice(0);
    this.filterWithValue = [...this.filterWithValue];
    this.filterChanged(this.filterWithValue);
  }

  colOrderChanged(event: TableColumnConfig[]) {
    this.visibleColumns = [];
    if (this.userView && event) {
      this.visibleColumns = event.filter(value => !value.hidden).map(value => value.field);
      this.storageService.set(CookieNames.CLEARING_VISIBLE_COLUMNS_ + this.userView.id + '_' + this.origin,
        JSON.stringify(this.visibleColumns));
    }
  }

  private clearPendingAsyncWork() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = undefined;
    }

    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
      this.reloadTimeout = undefined;
    }

    if (this.selectionInfoTimeout) {
      clearTimeout(this.selectionInfoTimeout);
      this.selectionInfoTimeout = undefined;
    }
  }

  private scheduleRefresh(action: () => void) {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = undefined;
      action();
    });
  }

  private scheduleReload() {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }

    this.reloadTimeout = setTimeout(() => {
      this.reloadTimeout = undefined;

      if (!this.selectionTradeDialogVisible || !this.tradeSelectionTable) {
        this.resetLoadingState();
        return;
      }

      this.tradeSelectionTable.setLoading();
      this.tradeSelectionTable.reload();
    });
  }

  private buildReloadKey(): string {
    const request = this.tradeTableCurrentRequest;

    return JSON.stringify({
      selectedTradeIds: this.selectedTradeIds ?? [],
      filterWithValue: this.filterWithValue ?? [],
      visibleColumns: this.visibleColumns ?? [],
      selectionBase: this.selectionBase,
      ctrlAMode: this.ctrlAMode,
      selectionType: this.selectionType,
      request: request ? {
        tabId: request.tabId,
        filters: request.filters,
        filters2: request.filters2,
        filters3: request.filters3,
        first: request.first,
        rows: request.rows,
        globalFilter: request.globalFilter,
        multiSortMeta: request.multiSortMeta,
        stateFilter: request['stateFilter'],
        sortOrder: request.sortOrder,
        sortField: request.sortField,
        visibleFields: request['visibleFields']
      } : undefined
    });
  }

  private splitAllocationEnabled(clearingSelectionInfosByLeg: ClearingSelectionInfo[]) {
    return this.tradeSelectionTable?.selectedTradeIds?.length <= this.appConfigService.allocationHardLimitCount
      && clearingSelectionInfosByLeg.length === 1 && !this.hasMultipleOwners(clearingSelectionInfosByLeg);
  }

  private singleAllocationEnabled(clearingSelectionInfosByLeg: ClearingSelectionInfo[]) {
    return this.tradeSelectionTable?.selectedTradeIds?.length > 0
      && this.tradeSelectionTable?.selectedTradeIds?.length <= this.appConfigService.allocationHardLimitCount
      && clearingSelectionInfosByLeg.length > 0 && !this.hasMultipleOwners(clearingSelectionInfosByLeg);
  }

  private applyEnabled() {
    return this.tradeSelectionTable?.selectedTradeIds?.length > 0
      && this.tradeSelectionTable?.selectedTradeIds?.length <= this.appConfigService.selectionApplyCountLimit;
  }

  private reloadClearingSectionInfos(ids: string[]) {
    if (!this.selectionTradeDialogVisible) {
      return;
    }

    const normalizedIds = ids ? [...ids] : [];

    if (this.sameIds(this.lastSelectionInfoIds, normalizedIds)) {
      return;
    }

    this.lastSelectionInfoIds = [...normalizedIds];

    this.splitAllocationEnabledValue = false;
    this.singleAllocationEnabledValue = false;
    this.applyEnabledValue = false;
    this.markForCheck();

    if (
      normalizedIds.length === 0 ||
      normalizedIds.length > this.appConfigService.allocationHardLimitCount
    ) {
      this.clearSelectionInfoRequest(false);
      return;
    }

    const request: ClearingInfoLoadRequest = new ClearingInfoLoadRequest([...normalizedIds]);
    const requestVersion = ++this.selectionInfoRequestVersion;

    this.selectionInfoSubscription?.unsubscribe();

    this.selectionInfoSubscription = this.clearingTradeInfoService
      .getSelectionInfoLegs(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(apiValidation => {
        if (requestVersion !== this.selectionInfoRequestVersion) {
          return;
        }

        if (apiValidation.success) {
          this.splitAllocationEnabledValue = this.splitAllocationEnabled(apiValidation.item);
          this.singleAllocationEnabledValue = this.singleAllocationEnabled(apiValidation.item);
          this.singleAllocationEnabledValue = this.applyEnabled();
          this.markForCheck();
        }
      });
  }

  hasMultipleOwners(clearingSelectionInfosByLeg: ClearingSelectionInfo[]): boolean {
    const owners = new Set(clearingSelectionInfosByLeg.map(x => x.owner));
    return owners.size > 1;
  }

  private sameIds(left?: string[], right?: string[]): boolean {
    if (left === right) {
      return true;
    }

    if (!left || !right || left.length !== right.length) {
      return false;
    }

    for (let i = 0; i < left.length; i++) {
      if (left[i] !== right[i]) {
        return false;
      }
    }

    return true;
  }

  private clearSelectionInfoRequest(resetSnapshot: boolean = false) {
    this.selectionInfoRequestVersion++;
    this.selectionInfoSubscription?.unsubscribe();
    this.selectionInfoSubscription = undefined;

    if (this.selectionInfoTimeout) {
      clearTimeout(this.selectionInfoTimeout);
      this.selectionInfoTimeout = undefined;
    }

    if (resetSnapshot) {
      this.lastSelectionInfoIds = undefined;
    }
  }

  private scheduleReloadClearingSectionInfos(ids: string[]) {
    if (this.selectionInfoTimeout) {
      clearTimeout(this.selectionInfoTimeout);
    }

    const idsSnapshot = ids ? [...ids] : [];

    this.selectionInfoTimeout = setTimeout(() => {
      this.selectionInfoTimeout = undefined;
      this.reloadClearingSectionInfos(idsSnapshot);
    }, 100);
  }

  private resetLoadingState() {
    this.tableLoading = false;
    this.tableLoadingChange.emit(false);
    this.tradeSelectionTable?.setNotLoading();
  }
}
